import { filter } from "~/tools/filter";
import { isNull } from "~/tools/is-null";
import { isPresent } from "~/tools/is-present";
import { map } from "~/tools/map";
import { omit } from "~/tools/omit";
import { pipe } from "~/tools/pipe";
import type { Record } from "~/tools/record";
import { unique } from "~/tools/unique";

import { type Boundaries, type Fragment, parse } from "./_parse";

export type TemplateVariableValue<TIrregular extends null = never> = TIrregular | string | number | boolean;

export type TemplateVariables<TIrregular extends null = never> = Record<string, TemplateVariableValue<TIrregular>>;

export interface TemplateInterpolateOptions {
	/**
	 * List of variable names to omit during interpolation.
	 */
	omit?: ReadonlyArray<string>;
}

export interface TemplateInterpolateOptionsWithFallback<TFallback> extends TemplateInterpolateOptions {
	whenVariableMissing: TFallback;
}

export class Template<TBoundaries extends Boundaries, TIrregularVariable extends null = never> {
	#fragments?: ReadonlyArray<Fragment>;

	public constructor(
		public readonly source: string,
		private readonly variables: TemplateVariables<TIrregularVariable>,
		private readonly boundaries: TBoundaries,
	) {}

	private getFragments(): ReadonlyArray<Fragment> {
		this.#fragments ??= parse(this.source, this.boundaries);

		return this.#fragments;
	}

	/**
	 * The JSON representation of the template after interpolation with its own variables.
	 * Used when calling `JSON.stringify` on the Template instance.
	 * Can be useful for 3rd-party libraries that rely on JSON serialization.
	 */
	protected toJSON(): unknown {
		return this.interpolate({});
	}

	/**
	 * Gets the unique placeholder names found in the template regardless of the variable values.
	 *
	 * @returns An array of unique placeholder names.
	 *
	 * @example
	 * const template = Template("Hello ${name}, your ID is ${id} and your name is ${name}.");
	 *
	 * template.getPlaceholders(); // ["name", "id"]
	 */
	public getPlaceholders(): Array<string> {
		return pipe(
			this.getFragments(),
			map((fragment) => ("variable" in fragment ? fragment.variable : null)),
			filter(isPresent),
			unique(),
		);
	}

	/**
	 * Adds new variables to the template, merging them with existing variables.
	 * Incoming variables take precedence over template's existing variables in case of key conflicts.
	 *
	 * @param variables - The variables to add to the template.
	 * @returns A new Template instance with the merged variables.
	 */
	public addVariables(variables: TemplateVariables<TIrregularVariable>): Template<TBoundaries, TIrregularVariable> {
		return new Template(this.source, { ...this.variables, ...variables }, this.boundaries);
	}

	/**
	 * Interpolates the template by replacing placeholders with corresponding variable values
	 * combined from the template's variables and the provided ones.
	 * Incoming variables take precedence over the template's variables in case of key conflicts.
	 *
	 * @template TFallback - The type of the fallback value when a variable is missing.
	 * @param variables - The variables to use for interpolation.
	 * @param options - The options for interpolation.
	 * @param options.whenVariableMissing - The fallback value to return when a variable is missing (value is `undefined`).
	 *
	 * @returns The interpolated string, or undefined if any variable value is `null`, or the fallback value when a variable is missing.
	 */
	public interpolate<TFallback>(
		variables: TemplateVariables<TIrregularVariable>,
		options: TemplateInterpolateOptionsWithFallback<TFallback>,
	): string | TFallback | (null extends TIrregularVariable ? undefined : never);

	/**
	 * Interpolates the template by replacing placeholders with corresponding variable values
	 * combined from the template's variables and the provided ones.
	 * Incoming variables take precedence over the template's variables in case of key conflicts.
	 *
	 * @param variables - The variables to use for interpolation.
	 * @param options - The options for interpolation.
	 * @returns The interpolated string, or undefined if any variable value is `null`.
	 */
	public interpolate(
		variables: TemplateVariables<TIrregularVariable>,
		options?: TemplateInterpolateOptions,
	): string | (null extends TIrregularVariable ? undefined : never);

	public interpolate<TFallback>(
		variables: TemplateVariables<TIrregularVariable>,
		options?: TemplateInterpolateOptionsWithFallback<TFallback>,
	): string | TFallback | undefined {
		const [left, right] = this.boundaries;
		const interpolated: Array<string> = [];
		const allVariables = omit({ ...this.variables, ...variables }, options?.omit ?? []) as Partial<
			TemplateVariables<TIrregularVariable>
		>;

		for (const fragment of this.getFragments()) {
			if ("text" in fragment) {
				interpolated.push(fragment.text);
			} else {
				const value = allVariables[fragment.variable];

				if (isNull(value)) {
					return undefined;
				}

				if (isPresent(value)) {
					interpolated.push(value.toString());
				} else if (options != null && "whenVariableMissing" in options) {
					return options.whenVariableMissing;
				} else {
					interpolated.push(`${left}${fragment.variable}${right}`);
				}
			}
		}

		return interpolated.join("");
	}
}
